// `@dutchie-pro-partners/theme-registry@0.1.0` declares peer `@dutchiesdk/ecommerce-extensions-sdk`
// `>=0.48.0 <0.49.0` (matching installed `^0.48.0`). Because shipped registry ESM bundles import
// `@dutchiesdk/ecommerce-extensions-sdk` (including `DataBridgeVersion`) as a runtime external from
// the host, `remoteComponentIsValid` compares the host's `DataBridgeVersion` against itself for
// registry themes — verify runtime ABI compatibility on any future SDK bump beyond `<0.49.0`.
import { findTheme } from '@dutchie-pro-partners/theme-registry';

export type ThemeModule = Record<string, unknown>;
type ThemeModuleWithDefault = ThemeModule & { default?: ThemeModule };

const themeModuleCache = new Map<string, ThemeModule>();
const inFlightLoads = new Map<string, Promise<ThemeModule | null>>();
const failedLoadPromises = new WeakSet<Promise<ThemeModule | null>>();

export function didThemeLoadFail(promise: Promise<ThemeModule | null>): boolean {
  return failedLoadPromises.has(promise);
}

const ADMIN_CUSTOMER_THEMES_SCOPE = '@dutchie/customer-themes';
const REGISTRY_CUSTOMER_THEMES_SCOPE = 'customer_themes';

// Admin and CMS store '@dutchie/customer-themes' in Mongo, while theme-registry keys by 'customer_themes'.
export function normalizeThemeScope(scope: string): string {
  return scope === ADMIN_CUSTOMER_THEMES_SCOPE ? REGISTRY_CUSTOMER_THEMES_SCOPE : scope;
}

export function getLoadedTheme(id: string): ThemeModule | undefined {
  return themeModuleCache.get(id);
}

export function setLoadedTheme(id: string, themeModule: ThemeModule): void {
  const candidate = themeModule as ThemeModuleWithDefault;
  const normalized = candidate.default ?? themeModule;
  themeModuleCache.set(id, normalized);
}

export function hasLoadedTheme(id: string): boolean {
  return themeModuleCache.has(id);
}

export function clearThemeCache(): void {
  themeModuleCache.clear();
  inFlightLoads.clear();
}

export function abandonInFlightLoad(id: string, promise: Promise<ThemeModule | null>): void {
  if (inFlightLoads.get(id) === promise) {
    inFlightLoads.delete(id);
  }
}

export type RegistryTheme = NonNullable<ReturnType<typeof findTheme>>;

// Not `async`: callers must receive the exact promise stored in inFlightLoads so that
// abandonInFlightLoad's identity check can match it.
export function loadTheme(record?: RegistryTheme | null): Promise<ThemeModule | null> {
  const cacheKey = record?.id ?? null;

  if (!cacheKey || !record || typeof record.load !== 'function') {
    return Promise.resolve(null);
  }

  if (themeModuleCache.has(cacheKey)) {
    return Promise.resolve(themeModuleCache.get(cacheKey) ?? null);
  }

  const inFlight = inFlightLoads.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  let promise: Promise<ThemeModule | null> | undefined;
  const state = { syncFailed: false };
  const markFailed = (): void => {
    if (promise) {
      failedLoadPromises.add(promise);
    } else {
      state.syncFailed = true;
    }
  };
  const cacheLoadedModule = (mod: ThemeModule | null): ThemeModule | null => {
    if (!mod || inFlightLoads.get(cacheKey) !== promise) {
      markFailed();
      return null;
    }
    setLoadedTheme(cacheKey, mod);
    return getLoadedTheme(cacheKey) ?? null;
  };
  const executeLoad = async (): Promise<ThemeModule | null> => {
    try {
      const isSsrReady = (record as { ssrReady?: boolean }).ssrReady;
      if (typeof window === 'undefined' && !isSsrReady) {
        return null;
      }
      return cacheLoadedModule(await record.load());
    } catch (error) {
      console.warn(`[theme-cache] Failed to load theme "${cacheKey}":`, error);
      markFailed();
      return null;
    }
  };
  promise = executeLoad();
  if (state.syncFailed) {
    failedLoadPromises.add(promise);
  }

  inFlightLoads.set(cacheKey, promise);

  void promise.finally(() => {
    if (inFlightLoads.get(cacheKey) === promise) {
      inFlightLoads.delete(cacheKey);
    }
  });

  return promise;
}
